import { Popover, Space, Typography } from "antd";
import * as React from "react";
import betLogo from "src/assets/logo/bet.svg";
import slotLogo from "src/assets/logo/slot.svg";
import sportLogo from "src/assets/logo/sport.svg";
import { OutlineInfo1 } from "src/icons";
import {
  WEBSITE_SYSTEM_PRODUCTS,
  WebsiteSystemProduct,
  WebsiteSystemProductEnabled,
  WebsiteSystemScope,
} from "src/services/websiteSystem";

// Site logos (logo only, no name text) shared by the table's "เว็บที่แสดง" column
// and the edit modal's site picker, so both always show the same mark.

export const WEBSITE_PRODUCT_LABEL: Record<WebsiteSystemProduct, string> = {
  PIG_SPIN: "PIGSLOT",
  PIG_BET: "PIGBET",
  PIG_SPORT: "PIGSPORT",
};

// Same three wordmarks CustomerStatistics shows per product.
const WEBSITE_PRODUCT_LOGO: Record<WebsiteSystemProduct, string> = {
  PIG_SPIN: slotLogo,
  PIG_BET: betLogo,
  PIG_SPORT: sportLogo,
};

// The row's selected sites in display order. Reads `selected`, not `enabled`:
// a row that is switched off still shows which sites it will come back on.
export const selectedWebsiteProducts = (products: WebsiteSystemProductEnabled[]): WebsiteSystemProduct[] =>
  WEBSITE_SYSTEM_PRODUCTS.filter((product) => products.some((entry) => entry.product === product && entry.selected));

export const WebsiteLogo: React.FC<{ product: WebsiteSystemProduct }> = ({ product }) => (
  <span data-product={product} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    {/* No name is rendered beside the logo, so alt is the only thing that says which site it is */}
    <img src={WEBSITE_PRODUCT_LOGO[product]} alt={WEBSITE_PRODUCT_LABEL[product]} style={{ height: 12 }} />
  </span>
);

// Spoken name of the "ทุกเว็บ (i)" trigger - the same three names the hover box lists.
const ALL_WEBSITES_ACCESSIBLE_NAME = `ทุกเว็บ: ${WEBSITE_SYSTEM_PRODUCTS.map((product) => WEBSITE_PRODUCT_LABEL[product]).join(", ")}`;

// Tip/Rain always run on every site, so instead of three logos they show
// "ทุกเว็บ" with an info icon; hovering or focusing it lists the three sites (no
// heading).
export const AllWebsitesLabel: React.FC = () => (
  <Popover
    trigger={["hover", "focus"]}
    content={
      <Space direction="vertical" size={8}>
        {WEBSITE_SYSTEM_PRODUCTS.map((product) => (
          <WebsiteLogo key={product} product={product} />
        ))}
      </Space>
    }
  >
    <span
      data-testid="all-websites"
      role="img"
      tabIndex={0}
      aria-label={ALL_WEBSITES_ACCESSIBLE_NAME}
      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      <Typography.Text>ทุกเว็บ</Typography.Text>
      <OutlineInfo1 />
    </span>
  </Popover>
);

export const WebsitesShown: React.FC<{ scope: WebsiteSystemScope; products: WebsiteSystemProductEnabled[] }> = ({
  scope,
  products,
}) => {
  if (scope === "ALL") return <AllWebsitesLabel />;
  const selected = selectedWebsiteProducts(products);
  if (selected.length === 0) return <Typography.Text type="secondary">-</Typography.Text>;
  return (
    <Space size={[12, 4]} wrap>
      {selected.map((product) => (
        <WebsiteLogo key={product} product={product} />
      ))}
    </Space>
  );
};
